
import {
  Vue, Component, Watch,
} from 'vue-property-decorator';
import { Lead as TLead, PermissionCode, ConfirmDialogOptions } from '@/types';
import API from '@/lib/API';
import { currentUserHasPermission } from '@/helpers';
import ConfirmDialog from '@/components/ConfirmDialog.vue';

@Component({
  components: {
    ConfirmDialog,
  },
})
export default class EturnityWaitingDialog extends Vue {
  private closeTimeout: number | null = null
  private shouldShowDialog = true
  private countdownTimer: number | null = null
  private countdownSeconds = 10
  private hasStartedCountdown = false
  private reopeningConfigurator = false
  private cancellingConfiguration = false
  private confirmCancelDialog = false
  private confirmCancelOptions: ConfirmDialogOptions | null = null

  get lead(): TLead {
    return this.$store.getters.currentLead;
  }

  get dialogVisible(): boolean {
    // Integrazione spenta: il dialog non si apre mai, nemmeno su una lead che
    // avesse i dati di una vecchia configurazione.
    if (!this.eturnityIntegration.enabled) return false;

    // Mostra il dialog solo se:
    // 1. È una lead di tipo standard (Eturnity)
    // 2. La configurazione è in corso (started, json_received o completed con ritardo)
    // 3. Il flag interno permette di mostrarlo
    if (!this.shouldShowDialog || !this.lead || this.lead.type !== 'standard') {
      return false;
    }

    // Se è completata, mostra solo se il countdown è ancora attivo E è stato avviato
    if (this.configStatus === 'completed') {
      return this.hasStartedCountdown && this.countdownSeconds > 0;
    }

    // Per gli stati in corso, mostra sempre il dialog (anche se c'era una config completata prima)
    if (['started', 'json_received'].includes(this.configStatus)) {
      return true;
    }

    return false;
  }

  get leadId(): string {
    return this.lead?.code || this.lead?.id?.toString() || 'N/A';
  }

  // Helper method per selezionare la configurazione con priorità più alta
  private getSelectedEturnityConfig(): { configId: string; status: string } | null {
    const customValues = this.lead?.CustomValues || [];

    const multipleConfigStatuses = customValues
      .filter((cv) => cv.name.startsWith('eturnityConfig_') && cv.name.endsWith('_status'));

    if (multipleConfigStatuses.length > 0) {
      // Prima cerca le configurazioni attive (started, json_received)
      const activeConfigs = multipleConfigStatuses.filter((cv) => ['started', 'json_received'].includes(cv.value || ''));

      // Se ci sono configurazioni attive, seleziona la più recente
      if (activeConfigs.length > 0) {
        const sortedByTime = activeConfigs.sort((a, b) => {
          const aTime = a.name.match(/eturnityConfig_\d+_(\d+)_status/)?.[1];
          const bTime = b.name.match(/eturnityConfig_\d+_(\d+)_status/)?.[1];
          return bTime && aTime ? parseInt(bTime, 10) - parseInt(aTime, 10) : 0;
        });

        const selectedConfig = sortedByTime[0];
        const configId = selectedConfig.name.replace('_status', '').replace('eturnityConfig_', '');
        return { configId, status: selectedConfig.value || 'Non impostato' };
      }

      // Se non ci sono configurazioni attive, fallback alle configurazioni completate
      const completedConfigs = multipleConfigStatuses.filter((cv) => cv.value === 'completed');
      if (completedConfigs.length > 0) {
        const sortedByTime = completedConfigs.sort((a, b) => {
          const aTime = a.name.match(/eturnityConfig_\d+_(\d+)_status/)?.[1];
          const bTime = b.name.match(/eturnityConfig_\d+_(\d+)_status/)?.[1];
          return bTime && aTime ? parseInt(bTime, 10) - parseInt(aTime, 10) : 0;
        });

        const selectedConfig = sortedByTime[0];
        const configId = selectedConfig.name.replace('_status', '').replace('eturnityConfig_', '');
        return { configId, status: selectedConfig.value || 'Non impostato' };
      }

      // Fallback generico (non dovrebbe mai accadere)
      const selectedConfig = multipleConfigStatuses[0];
      const configId = selectedConfig.name.replace('_status', '').replace('eturnityConfig_', '');
      return { configId, status: selectedConfig.value || 'Non impostato' };
    }

    return null;
  }

  get configStatus(): string {
    const selectedConfig = this.getSelectedEturnityConfig();

    if (selectedConfig) {
      // eslint-disable-next-line no-console
      console.log('EturnityWaitingDialog: configStatus() - selected status:', selectedConfig.status, 'from config:', selectedConfig.configId);
      return selectedConfig.status;
    }

    // Nessuna configurazione Eturnity trovata
    // eslint-disable-next-line no-console
    console.log('EturnityWaitingDialog: configStatus() - no config found');
    return 'Non impostato';
  }

  get configStartedAt(): string | Date | null {
    const customValues = this.lead?.CustomValues || [];
    const selectedConfig = this.getSelectedEturnityConfig();

    if (selectedConfig) {
      const startedValue = customValues.find((cv) => cv.name === `eturnityConfig_${selectedConfig.configId}_startedAt`);
      return startedValue?.dateValue || null;
    }

    return null;
  }

  get configCompletedAt(): string | Date | null {
    const customValues = this.lead?.CustomValues || [];
    const selectedConfig = this.getSelectedEturnityConfig();

    if (selectedConfig) {
      const completedValue = customValues.find((cv) => cv.name === `eturnityConfig_${selectedConfig.configId}_completedAt`);
      return completedValue?.dateValue || null;
    }

    return null;
  }

  get pdfReference(): string | null {
    const customValues = this.lead?.CustomValues || [];
    const selectedConfig = this.getSelectedEturnityConfig();

    if (selectedConfig) {
      const referenceValue = customValues.find((cv) => cv.name === `eturnityConfig_${selectedConfig.configId}_pdfReference`);
      return referenceValue?.value || null;
    }

    return null;
  }

  get statusTitle(): string {
    switch (this.configStatus) {
      case 'started':
        return 'Elaborazione in corso';
      case 'json_received':
        return 'Recupero riepilogo in PDF';
      case 'completed':
        return 'Configurazione completata';
      default:
        return 'Stato sconosciuto';
    }
  }

  get isMultipleConfig(): boolean {
    const customValues = this.lead?.CustomValues || [];
    return customValues.some((cv) => cv.name.startsWith('eturnityConfig_') && cv.name.endsWith('_status'));
  }

  get eturnityIntegration() {
    return this.$store.getters.getIntegrations.eturnity;
  }

  get canReopenConfigurator(): boolean {
    // L'integrazione e' spenta: il permesso da solo non basta, i Super Utenti
    // lo scavalcano.
    if (!this.eturnityIntegration.enabled) return false;

    // Verifica se l'utente ha il permesso per creare configurazioni da lead esistenti
    if (!currentUserHasPermission('eturnity.create_config_from_lead' as PermissionCode)) {
      return false;
    }

    // Verifica che la lead sia di tipo standard e in stato open
    if (!this.lead || this.lead.type !== 'standard' || this.lead.status !== 'open') {
      return false;
    }

    // Verifica che ci sia un Task P (Preventivo) in lavorazione
    const taskP = this.lead.Tasks?.find((task) => task.name === 'P' && task.status === null);
    if (!taskP) {
      return false;
    }

    // Il pulsante è visibile solo se la configurazione non è completata
    // e non è ancora arrivata alla fase di elaborazione completata (json_received)
    return this.configStatus !== 'completed' && this.configStatus !== 'json_received';
  }

  get canCancelConfiguration(): boolean {
    // Il pulsante di annullamento è visibile solo se:
    // 1. C'è una lead attiva
    // 2. La configurazione non è ancora completata
    // 3. La configurazione è stata avviata (started o json_received)
    if (!this.lead || this.configStatus === 'completed') {
      return false;
    }

    return ['started', 'json_received'].includes(this.configStatus);
  }

  get statusDescription(): string {
    switch (this.configStatus) {
      case 'started':
        return 'Il calcolatore solare di Eturnity è stato avviato. '
          + 'Il sistema sta elaborando la configurazione.';
      case 'json_received':
        return 'La configurazione è stata elaborata. '
          + 'Il sistema sta recuperando il riepilogo in PDF.';
      case 'completed':
        return 'La configurazione è stata completata con successo. '
          + 'Tutti i dati e il riepilogo PDF sono stati ricevuti. '
          + 'La lead è stata aggiornata automaticamente.';
      default:
        return 'Stato della configurazione non riconosciuto.';
    }
  }

  getStepColor(status: string): string {
    switch (status) {
      case 'started': {
        // La fase "configurazione avviata" è sempre completata
        return 'green';
      }
      case 'json_received': {
        if (this.configStatus === 'started') return 'orange';
        if (this.configStatus === 'json_received' || this.configStatus === 'completed') {
          return 'green';
        }
        return 'grey';
      }
      case 'pdf_waiting': {
        if (this.configStatus === 'json_received') return 'orange';
        if (this.configStatus === 'completed') return 'green';
        return 'grey';
      }
      case 'completed': {
        return this.configStatus === 'completed' ? 'green' : 'grey';
      }
      default:
        return 'grey';
    }
  }

  getStepIcon(status: string): string {
    switch (status) {
      case 'started': {
        // La fase "configurazione avviata" è sempre completata
        return 'mdi-check-circle';
      }
      case 'json_received': {
        if (this.configStatus === 'started') return 'mdi-clock-outline';
        if (this.configStatus === 'json_received' || this.configStatus === 'completed') {
          return 'mdi-check-circle';
        }
        return 'mdi-circle-outline';
      }
      case 'pdf_waiting': {
        if (this.configStatus === 'json_received') return 'mdi-file-document-outline';
        if (this.configStatus === 'completed') return 'mdi-check-circle';
        return 'mdi-circle-outline';
      }
      case 'completed': {
        return this.configStatus === 'completed' ? 'mdi-check-circle' : 'mdi-circle-outline';
      }
      default:
        return 'mdi-circle-outline';
    }
  }

  formatDate(date: string | Date | null): string {
    if (!date) return 'Non impostata';

    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleString('it-IT', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch (error) {
      return 'Data non valida';
    }
  }

  closeDialog() {
    // Chiude il dialog immediatamente quando l'utente clicca il pulsante
    this.clearCloseTimeout();
    this.forceCloseDialog();
  }

  async reopenConfigurator() {
    if (!this.lead || this.reopeningConfigurator) {
      return;
    }

    this.reopeningConfigurator = true;

    try {
      // Trova la configurazione esistente in stato "started" da riutilizzare
      const selectedConfig = this.getSelectedEturnityConfig();
      if (!selectedConfig || selectedConfig.status !== 'started') {
        // Se non c'è una configurazione in stato "started", crea una nuova configurazione
        const response = await API.execute('post', `/lead/${this.lead.id}/eturnity-config`);

        if (response.success && response.configId) {
          // Apre il calcolatore solare di Eturnity in una nuova scheda.
          // Indirizzo dal backend, non cablato.
          const eturnityUrl = `${this.eturnityIntegration.calculatorUrl}?utm_source=${response.configId}`;
          window.open(eturnityUrl, '_blank');

          // Aggiorna la lead corrente per far apparire l'aggiornamento del dialog
          // eslint-disable-next-line no-console
          console.log('Calcolatore solare Eturnity riaperto per nuova configurazione:', response.configId);
          await this.$store.dispatch('updateProject', { id: this.lead.id });

          // Mostra un messaggio di successo
          this.$store.dispatch('showAlert', {
            type: 'success',
            text: 'Configuratore Eturnity riaperto con successo. Una nuova configurazione è stata avviata.',
          });
        } else {
          // eslint-disable-next-line no-console
          console.error('Errore nella risposta del server:', response);
          this.$store.dispatch('showAlert', {
            type: 'error',
            text: 'Errore durante la riapertura del configuratore. Riprova più tardi.',
          });
        }
      } else {
        // Riutilizza la configurazione esistente in stato "started"
        const { configId } = selectedConfig;

        // Apre il calcolatore solare di Eturnity in una nuova scheda con il
        // configId esistente. Indirizzo dal backend, non cablato.
        const eturnityUrl = `${this.eturnityIntegration.calculatorUrl}?utm_source=${configId}`;
        window.open(eturnityUrl, '_blank');

        // eslint-disable-next-line no-console
        console.log('Calcolatore solare Eturnity riaperto per configurazione esistente:', configId);

        // Mostra un messaggio di successo
        this.$store.dispatch('showAlert', {
          type: 'success',
          text: 'Configuratore Eturnity riaperto con successo. La configurazione esistente è stata riutilizzata.',
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Errore durante la riapertura del configuratore Eturnity:', error);

      // Mostra un messaggio di errore all'utente
      if ((error as Error & { response?: { data?: { error?: string } } })?.response?.data?.error) {
        this.$store.dispatch('showAlert', {
          type: 'error',
          text: (error as Error & { response: { data: { error: string } } }).response.data.error,
        });
      } else {
        this.$store.dispatch('showAlert', {
          type: 'error',
          text: 'Errore durante la riapertura del calcolatore solare. Riprova più tardi.',
        });
      }
    } finally {
      this.reopeningConfigurator = false;
    }
  }

  showCancelConfirmDialog() {
    if (!this.lead) {
      return;
    }

    const isLeadDraft = this.lead.status === 'draft';

    let title = 'Annulla Configurazione Eturnity';
    let text: string[] = [];
    let question = 'Vuoi continuare?';

    if (isLeadDraft) {
      // Lead draft - la lead verrà eliminata completamente
      title = 'Annulla Configurazione e Elimina Lead';
      text = [
        'Stai per annullare la configurazione Eturnity in corso.',
        'Poiché questa lead è stata creata tramite il configuratore e non è ancora stata completata, verrà eliminata completamente.',
        'Tutti i dati della lead andranno persi definitivamente.',
      ];
      question = 'Sei sicuro di voler eliminare la lead?';
    } else {
      // Lead esistente - verranno rimossi solo i custom values delle configurazioni non completate
      title = 'Annulla Configurazione Eturnity';
      text = [
        'Stai per annullare la configurazione Eturnity in corso.',
        'Verranno rimossi i dati relativi alle configurazioni non completate.',
        'La lead e le configurazioni completate (se presenti) rimarranno invariate.',
      ];
      question = 'Vuoi annullare la configurazione?';
    }

    this.confirmCancelOptions = {
      title,
      text,
      question,
      yesText: 'Sì, annulla',
      noText: 'No, mantieni',
    };

    this.confirmCancelDialog = true;
  }

  async cancelConfiguration() {
    if (!this.lead || this.cancellingConfiguration) {
      return;
    }

    this.cancellingConfiguration = true;

    try {
      // Chiama il nuovo endpoint per annullare la configurazione Eturnity
      const response = await API.execute('delete', `/lead/${this.lead.id}/eturnity-config`);

      if (response.success) {
        if (response.leadDeleted) {
          // La lead draft è stata eliminata
          // Il WebSocket gestirà automaticamente la deselezione della lead
          // Non chiamiamo unselectLead() manualmente per evitare conflitti

          // Chiude il dialog immediatamente
          this.forceCloseDialog();

          // Naviga alla lista delle lead usando window.location per evitare problemi di routing
          // Questo bypassa il sistema di routing di Vue che potrebbe causare problemi
          setTimeout(() => {
            window.location.href = '/leads';
          }, 100);
        } else {
          // Chiude il dialog dopo l'annullamento
          this.forceCloseDialog();

          // Aggiorna la lead corrente per riflettere le modifiche
          await this.$store.dispatch('updateProject', { id: this.lead.id });

          // Mostra un messaggio di successo
          this.$store.dispatch('showAlert', {
            type: 'success',
            text: response.message || 'Configurazione Eturnity annullata con successo.',
          });
        }
      } else {
        throw new Error(response.error || 'Errore durante l\'annullamento');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Errore durante l\'annullamento della configurazione Eturnity:', error);

      // Mostra un messaggio di errore all'utente
      if ((error as Error & { response?: { data?: { error?: string } } })?.response?.data?.error) {
        this.$store.dispatch('showAlert', {
          type: 'error',
          text: (error as Error & { response: { data: { error: string } } }).response.data.error,
        });
      } else {
        this.$store.dispatch('showAlert', {
          type: 'error',
          text: 'Errore durante l\'annullamento della configurazione. Riprova più tardi.',
        });
      }
    } finally {
      this.cancellingConfiguration = false;
    }
  }

  private clearCloseTimeout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private forceCloseDialog() {
    // Forza la chiusura del dialog impostando il flag interno
    this.shouldShowDialog = false;
  }

  @Watch('configStatus')
  onConfigStatusChange(newStatus: string, oldStatus: string) {
    // eslint-disable-next-line no-console
    console.log('EturnityWaitingDialog: configStatus changed from', oldStatus, 'to', newStatus);

    // Reimposta shouldShowDialog quando viene rilevata una nuova configurazione attiva
    // Questo risolve il bug per cui il dialog non si apriva se era stato chiuso precedentemente
    if (['started', 'json_received'].includes(newStatus) && !['started', 'json_received'].includes(oldStatus)) {
      // eslint-disable-next-line no-console
      console.log('EturnityWaitingDialog: New active configuration detected, enabling dialog');
      this.shouldShowDialog = true;
    }

    // Se la configurazione è completata, avvia un timer per chiudere il dialog
    // SOLO se il cambio è da un stato attivo (started/json_received) a completed
    if (newStatus === 'completed') {
      if (oldStatus === 'started' || oldStatus === 'json_received') {
        // eslint-disable-next-line no-console
        console.log('EturnityWaitingDialog: Configuration just completed, dialog will close in 10 seconds');
        this.hasStartedCountdown = true;
        this.startCountdown();
      } else {
        // eslint-disable-next-line no-console
        console.log('EturnityWaitingDialog: Configuration was already completed or no previous active state');
      }
    }

    // Reset del countdown se torna a uno stato attivo
    if (['started', 'json_received'].includes(newStatus) && oldStatus === 'completed') {
      // eslint-disable-next-line no-console
      console.log('EturnityWaitingDialog: New active configuration detected, resetting countdown');
      this.hasStartedCountdown = false;
      this.clearCloseTimeout();
    }
  }

  @Watch('lead')
  onLeadChange() {
    // eslint-disable-next-line no-console
    console.log('EturnityWaitingDialog: lead changed, forcing reactivity update');

    // Forza la reattività del componente quando la lead viene aggiornata
    // Questo è necessario perché i computed properties potrebbero non rilevare
    // i cambiamenti nei custom values quando vengono aggiornati via WebSocket
    this.$forceUpdate();
  }

  @Watch('lead.CustomValues')
  onCustomValuesChange(newCustomValues: any[]) {
    // eslint-disable-next-line no-console
    console.log('EturnityWaitingDialog: CustomValues changed, checking for Eturnity updates');

    // Verifica se ci sono stati cambiamenti nei custom values Eturnity (formato unificato)
    const eturnityCustomValues = newCustomValues?.filter((cv) => cv.name.startsWith('eturnityConfig_')) || [];

    if (eturnityCustomValues.length > 0) {
      // eslint-disable-next-line no-console
      console.log('EturnityWaitingDialog: Eturnity custom values detected:', eturnityCustomValues.map((cv) => ({
        name: cv.name,
        value: cv.value,
        dateValue: cv.dateValue,
      })));

      // Forza l'aggiornamento del componente
      this.$forceUpdate();
    }
  }

  mounted() {
    // eslint-disable-next-line no-console
    console.log('EturnityWaitingDialog: mounted() with current configStatus =', this.configStatus);
  }

  beforeDestroy() {
    // Pulisce il timer quando il componente viene distrutto
    this.clearCloseTimeout();
  }

  private startCountdown() {
    // Cancella eventuali timer precedenti
    this.clearCloseTimeout();

    // Reset del countdown
    this.countdownSeconds = 10;

    // Avvia il countdown visivo
    this.countdownTimer = window.setInterval(() => {
      this.countdownSeconds -= 1;
      if (this.countdownSeconds <= 0) {
        this.clearCloseTimeout();
        // Forza la chiusura del dialog quando il countdown raggiunge 0
        this.forceCloseDialog();
      }
    }, 1000);

    // Imposta un timer di 10 secondi per chiudere il dialog
    this.closeTimeout = window.setTimeout(() => {
      this.forceCloseDialog();
      // eslint-disable-next-line no-console
      console.log('EturnityWaitingDialog: Dialog closed automatically after delay');
    }, 10000);
  }
}
